import React from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { cn } from '../../../utils/cn';

export interface MachineFilterOption {
  id: string;
  label: string;
}

export interface MachineTypeFilterOption {
  id: string;
  label: string;
  machineName?: string;
}

interface WorkOrdersFiltersProps {
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  statusFilter: string[];
  setStatusFilter: (value: string[]) => void;
  roastTypeFilter: string[];
  setRoastTypeFilter: (value: string[]) => void;
  machineFilter: string[];
  setMachineFilter: (value: string[]) => void;
  machineTypeFilter: string[];
  setMachineTypeFilter: (value: string[]) => void;
  statusOptions: string[];
  roastTypeOptions: string[];
  roastTypeLabelMap: Record<string, string>;
  machineOptions: MachineFilterOption[];
  machineTypeOptions: MachineTypeFilterOption[];
}

export const WorkOrdersFilters: React.FC<WorkOrdersFiltersProps> = ({
  searchFilter,
  setSearchFilter,
  statusFilter,
  setStatusFilter,
  roastTypeFilter,
  setRoastTypeFilter,
  machineFilter,
  setMachineFilter,
  machineTypeFilter,
  setMachineTypeFilter,
  statusOptions,
  roastTypeOptions,
  roastTypeLabelMap,
  machineOptions,
  machineTypeOptions,
}) => {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = React.useState(false);

  const machineLabelMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    machineOptions.forEach(m => { map[m.id] = m.label; });
    return map;
  }, [machineOptions]);

  const machineTypeLabelMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    machineTypeOptions.forEach(mt => { map[mt.id] = mt.label; });
    return map;
  }, [machineTypeOptions]);

  const activeFilterCount =
    (statusFilter.length > 0 ? 1 : 0) +
    (roastTypeFilter.length > 0 ? 1 : 0) +
    (machineFilter.length > 0 ? 1 : 0) +
    (machineTypeFilter.length > 0 ? 1 : 0);

  return (
    <div className="mb-4">
      <div className="flex flex-col md:flex-row md:flex-nowrap md:items-end gap-3 lg:gap-4">
        <div className="flex-1 min-w-0">
          <label htmlFor="searchFilter" className="block text-sm font-medium text-gray-700">
            {t('workOrders.filters.search')}
          </label>
          <div className="flex gap-2 mt-1">
            <input
              data-test-id="wo-search-input"
              type="text"
              id="searchFilter"
              placeholder={t('workOrders.filters.searchPlaceholder')}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            />
            <button
              type="button"
              onClick={() => setIsExpanded(v => !v)}
              aria-expanded={isExpanded}
              data-test-id="wo-filters-toggle"
              className="md:hidden shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              <span>{t('workOrders.filters.toggleFilters')}</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div
          className={cn(
            'w-full md:w-auto md:flex md:flex-row md:flex-nowrap md:items-end md:gap-2 lg:gap-3',
            isExpanded ? 'flex flex-col gap-4' : 'hidden'
          )}
        >
          <div className="w-full md:w-24 lg:w-32 xl:w-40">
            <label className="block text-sm font-medium text-gray-700">{t('workOrders.filters.status')}</label>
            <Listbox value={statusFilter} onChange={setStatusFilter} multiple>
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id="wo-status-filter"
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-2.5 pr-7 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate">
                    {statusFilter.length === 0
                      ? t('workOrders.filters.allStatuses')
                      : statusFilter.length === 1
                        ? statusFilter[0].replace('_', ' ')
                        : t('workOrders.filters.selectedCount', { count: statusFilter.length })}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {statusOptions.map((status) => (
                    <ListboxOption
                      key={status}
                      value={status}
                      className={({ focus }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate capitalize ${selected ? 'font-medium' : 'font-normal'}`}>
                            {status.replace('_', ' ')}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          <div className="w-full md:w-24 lg:w-32 xl:w-40">
            <label className="block text-sm font-medium text-gray-700">{t('workOrders.filters.roastType')}</label>
            <Listbox value={roastTypeFilter} onChange={setRoastTypeFilter} multiple>
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id="wo-roast-type-filter"
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-2.5 pr-7 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate">
                    {roastTypeFilter.length === 0
                      ? t('workOrders.filters.allRoastTypes')
                      : roastTypeFilter.length === 1
                        ? roastTypeLabelMap[roastTypeFilter[0]] ?? roastTypeFilter[0]
                        : t('workOrders.filters.selectedCount', { count: roastTypeFilter.length })}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {roastTypeOptions.map((type) => (
                    <ListboxOption
                      key={type}
                      value={type}
                      className={({ focus }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {roastTypeLabelMap[type] ?? type}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          <div className="w-full md:w-24 lg:w-32 xl:w-40">
            <label className="block text-sm font-medium text-gray-700">{t('workOrders.filters.machine')}</label>
            <Listbox value={machineFilter} onChange={setMachineFilter} multiple>
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id="wo-machine-filter"
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-2.5 pr-7 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate">
                    {machineFilter.length === 0
                      ? t('workOrders.filters.allMachines')
                      : machineFilter.length === 1
                        ? machineLabelMap[machineFilter[0]] ?? machineFilter[0]
                        : t('workOrders.filters.selectedCount', { count: machineFilter.length })}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {machineOptions.length === 0 ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-500 italic text-center">
                      {t('workOrders.filters.allMachines')}
                    </div>
                  ) : (
                    machineOptions.map((machine) => (
                      <ListboxOption
                        key={machine.id}
                        value={machine.id}
                        className={({ focus }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {machine.label}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                                <Check className="h-4 w-4" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))
                  )}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          <div className="w-full md:w-24 lg:w-32 xl:w-40">
            <label className="block text-sm font-medium text-gray-700">{t('workOrders.filters.machineType')}</label>
            <Listbox value={machineTypeFilter} onChange={setMachineTypeFilter} multiple>
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id="wo-machine-type-filter"
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-2.5 pr-7 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate">
                    {machineTypeFilter.length === 0
                      ? t('workOrders.filters.allMachineTypes')
                      : machineTypeFilter.length === 1
                        ? machineTypeLabelMap[machineTypeFilter[0]] ?? machineTypeFilter[0]
                        : t('workOrders.filters.selectedCount', { count: machineTypeFilter.length })}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {machineTypeOptions.length === 0 ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-500 italic text-center">
                      {t('workOrders.filters.allMachineTypes')}
                    </div>
                  ) : (
                    machineTypeOptions.map((mt) => (
                      <ListboxOption
                        key={mt.id}
                        value={mt.id}
                        className={({ focus }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {mt.label}
                              {machineFilter.length === 0 && mt.machineName ? (
                                <span className="text-gray-500"> — {mt.machineName}</span>
                              ) : null}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                                <Check className="h-4 w-4" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))
                  )}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
        </div>
      </div>
    </div>
  );
};
